import React, {
  createContext,
  type FC,
  useEffect,
  useState,
  useRef,
  useCallback,
} from 'react';
import { usePageNavigate } from '../hooks/usePageNavigate';
import type { AppTemplate } from '@wix/ambassador-devcenter-apps-v1-app-template/types';
import { QueryParams } from '../constants/QueryParams';
import {
  isValidAppliedFilter,
  urlParamToFilterMapper,
} from '../utils/urlParams-utils';
import { useTemplatesPageBi } from '../hooks/useTemplatesPageBi';
import { NonTypeTab, type TemplateTab } from '../types/TemplateTabs';
import { useQueryParams } from '../hooks/useQueryParams';
import { isAllTabInUse } from '../selectors/tabs-selectors';
import { DevelopmentTool } from '@wix/ambassador-devcenter-apps-v1-app-template/types';
import { getTemplateTabs } from '../constants/sideBarItems';
import { OBSERVER_CONFIG } from '../constants/intersectionObserver';
import {
  findMostVisibleSection,
  findTabIdForSection,
  getMinThresholdForTab,
} from '../utils/intersectionObserverHelpers';
import { useExperiments } from '@wix/fe-essentials/react';
import { shouldDisplayHarmonyNotifications } from '../utils/experiments';
import { useTemplatesData } from '../hooks/useTemplatesData';
import { useDebounce } from '../hooks/useDebounce';

export interface ITemplatesTabsContext {
  activeTabId: TemplateTab;
  activeTabIdDebounced: TemplateTab;
  onTabClick: (
    tabId: TemplateTab,
    mainGridRef: React.RefObject<HTMLDivElement>,
    shouldScrollIntoView: boolean,
  ) => void;
  shouldDisplayTemplateByTab: (template: AppTemplate) => boolean;
  sectionRefs: Record<DevelopmentTool, React.RefObject<HTMLDivElement>>;
  setupIntersectionObserver: () => (() => void) | undefined;
}

export const TemplatesTabsContext = createContext<ITemplatesTabsContext>({
  activeTabId: NonTypeTab.ALL,
  activeTabIdDebounced: NonTypeTab.ALL,
  onTabClick: () => {},
  shouldDisplayTemplateByTab: () => true,
  sectionRefs: {} as Record<DevelopmentTool, React.RefObject<HTMLDivElement>>,
  setupIntersectionObserver: () => undefined,
});

export const TemplatesTabsContextProvider: FC = ({ children }) => {
  const { appliedFilter, setParamAccordingToTab, shouldHideSidebar } =
    usePageNavigate();
  const { removeParam, getParam } = useQueryParams();
  const [activeTabId, setActiveTabId] = useState<TemplateTab>(
    isValidAppliedFilter(appliedFilter) ? appliedFilter : NonTypeTab.ALL,
  );
  const activeTabIdDebounced = useDebounce(activeTabId, 200);
  const { reportClickOnTab } = useTemplatesPageBi();
  const isAllTab = isAllTabInUse(activeTabId);
  const { experiments } = useExperiments({ readOnly: true });
  const displayHarmonyNotifications =
    shouldDisplayHarmonyNotifications(experiments);
  const { templatesLoaded } = useTemplatesData();

  const templatesTabs = getTemplateTabs(displayHarmonyNotifications);

  const blocksRef = useRef<HTMLDivElement>(null);
  const cliRef = useRef<HTMLDivElement>(null);
  const devCenterRef = useRef<HTMLDivElement>(null);

  const sectionRefs = React.useMemo(
    () => ({
      [DevelopmentTool.BLOCKS]: blocksRef,
      [DevelopmentTool.CLI]: cliRef,
      [DevelopmentTool.DEV_CENTER]: devCenterRef,
      [DevelopmentTool.UNKNOWN_TEMPLATE_TYPE]: {
        current: null,
      },
    }),
    [blocksRef, cliRef, devCenterRef],
  );

  const onTabClick = (
    tabId: TemplateTab,
    mainGridRef: React.RefObject<HTMLDivElement>,
    shouldScrollIntoView: boolean,
  ) => {
    reportClickOnTab(tabId);
    setActiveTabId(tabId);
    setParamAccordingToTab(tabId);
    shouldScrollIntoView &&
      mainGridRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const shouldDisplayTemplateByTab = (template: AppTemplate): boolean =>
    isAllTab || !!template?.developmentTools?.includes(activeTabId);

  const handleSectionVisibilityChange = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const mostVisibleSection = findMostVisibleSection(entries);
      if (!mostVisibleSection) {
        return;
      }
      const tabId = findTabIdForSection(
        mostVisibleSection.target,
        sectionRefs,
        templatesTabs,
      );
      const minThreshold = getMinThresholdForTab(tabId);
      const shouldUpdateActiveTab =
        tabId &&
        tabId !== activeTabId &&
        mostVisibleSection.intersectionRatio >= minThreshold &&
        !shouldHideSidebar;
      if (shouldUpdateActiveTab) {
        setActiveTabId(tabId);
      }
    },
    [sectionRefs, activeTabId, templatesTabs],
  );

  const setupIntersectionObserver = () => {
    if (!templatesLoaded) {
      return;
    }

    const observer = new IntersectionObserver(
      handleSectionVisibilityChange,
      OBSERVER_CONFIG,
    );

    templatesTabs.forEach((tabId) => {
      const sectionElement = sectionRefs[tabId].current;
      if (sectionElement) {
        observer.observe(sectionElement);
      }
    });

    return () => {
      observer.disconnect();
    };
  };

  useEffect(() => {
    if (!templatesLoaded) {
      return;
    }
    const filterParam = getParam(QueryParams.FILTER);
    const targetTool = filterParam && urlParamToFilterMapper[filterParam];
    const targetRef = sectionRefs[targetTool];
    if (targetTool) {
      const scrollAnchor = targetRef.current.querySelector(
        `#scroll-anchor-${targetTool}`,
      );
      scrollAnchor.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [getParam, sectionRefs, templatesLoaded]);

  useEffect(() => {
    const isValidFilter = appliedFilter && isValidAppliedFilter(appliedFilter);
    if (!isValidFilter) {
      removeParam(QueryParams.FILTER);
    }
  }, [removeParam, appliedFilter]);

  return (
    <TemplatesTabsContext.Provider
      value={{
        activeTabId,
        activeTabIdDebounced,
        onTabClick,
        shouldDisplayTemplateByTab,
        sectionRefs,
        setupIntersectionObserver,
      }}
    >
      {children}
    </TemplatesTabsContext.Provider>
  );
};
